import React, { createContext, useReducer, Dispatch, useCallback } from 'react'
import {
  MaintenanceReducer,
  MaintenanceState,
  MaintenanceAction,
} from './maintenance-reducer'
import { PartexVehicleVariant } from '@cinch-labs/data-consumer-valuations'
import {
  AppointmentType,
  ContactTimePreference,
  CustomerAddress,
  ServiceOptions,
} from '../types'

interface MaintenanceContextProps {
  state: MaintenanceState
  dispatch: Dispatch<MaintenanceAction>
  handleBack: () => void
  handleNext: () => void
  postCode?: string
}

const MaintenanceContext = createContext<MaintenanceContextProps | undefined>(
  undefined,
)

export const MaintenanceProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const initialState: MaintenanceState = {
    step: 1,
    postcode: undefined,
    foundVariants: [],
    searchedVrm: '',
    canBookAppointment: false,
  }

  const [state, dispatch] = useReducer(MaintenanceReducer, initialState)

  const handleBack = useCallback(() => {
    dispatch({ type: 'PREVIOUS_STEP' })
  }, [dispatch])

  const handleNext = useCallback(() => {
    dispatch({ type: 'NEXT_STEP' })
  }, [dispatch])

  return (
    <MaintenanceContext.Provider
      value={{
        state,
        dispatch,
        handleBack,
        handleNext,
      }}
    >
      {children}
    </MaintenanceContext.Provider>
  )
}

export interface MaintenanceContext {
  dispatch: Dispatch<MaintenanceAction>
  step: number
  customerTitle?: string
  customerFirstName?: string
  customerSurname?: string
  customerEmail?: string
  customerAddress?: CustomerAddress
  store?: string
  vehicleMake?: string
  vehicleModel?: string
  vehicleVin?: string
  vehicleFuelType?: string
  vrm?: string
  notes?: string
  storeId?: string
  foundVariants?: PartexVehicleVariant[]
  searchedVrm?: string
  selectedFoundVariantId?: string | null
  serviceOptions?: ServiceOptions
  serviceOptionsSelectedNames?: string
  serviceOptionsSelectedTotalPrice?: string
  customerPhone?: string
  phonePermissions?: boolean
  smsPermissions?: boolean
  emailPermissions?: boolean
  streetAddress?: string
  postcode?: string
  bookingDate?: string
  bookingTime?: string
  appointmentType?: AppointmentType
  appointmentTypeId?: number
  contactTimePreference?: ContactTimePreference
  canBookAppointment?: boolean
  dealerId?: string
  vin?: string
  mileage?: number
  handleBack: () => void
  handleNext: () => void
}

export const useMaintenanceContext = (): MaintenanceContext => {
  const context = React.useContext(MaintenanceContext)
  if (!context) {
    throw new Error(
      'useMaintenanceContext must be used within a MaintenanceProvider',
    )
  }
  const { state, dispatch } = context
  const {
    step,
    customerTitle,
    customerFirstName,
    customerSurname,
    customerEmail,
    customerAddress,
    store,
    vehicleMake,
    vehicleModel,
    vehicleVin,
    vehicleFuelType,
    vrm,
    mileage,
    notes,
    storeId,
    streetAddress,
    postcode,
    serviceOptions,
    serviceOptionsSelectedNames,
    serviceOptionsSelectedTotalPrice,
    foundVariants,
    searchedVrm,
    selectedFoundVariantId,
    customerPhone,
    emailPermissions,
    bookingDate,
    bookingTime,
    appointmentType,
    appointmentTypeId,
    contactTimePreference,
    canBookAppointment,
    dealerId,
    vin,
  } = state

  return {
    dispatch,
    step,
    customerTitle,
    customerFirstName,
    customerSurname,
    customerEmail,
    customerAddress,
    store,
    storeId,
    vehicleMake,
    vehicleModel,
    vehicleVin,
    vehicleFuelType,
    vrm,
    mileage,
    notes,
    serviceOptions,
    serviceOptionsSelectedNames,
    serviceOptionsSelectedTotalPrice,
    postcode,
    foundVariants,
    searchedVrm,
    selectedFoundVariantId,
    customerPhone,
    streetAddress,
    emailPermissions,
    bookingDate,
    bookingTime,
    appointmentType,
    appointmentTypeId,
    contactTimePreference,
    canBookAppointment,
    dealerId,
    vin,
    handleBack: context.handleBack,
    handleNext: context.handleNext,
  }
}
